import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { t } from "@/lib/i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { toast } from "sonner";

function googleSignIn() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Register() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", company: "", password: "", language: lang });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data);
      toast.success("Account created");
      navigate("/dashboard");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-12">
      <div className="col-span-1 lg:col-span-6 bg-white flex flex-col">
        <header className="flex items-center justify-between p-6 border-b border-[#E9ECEF]">
          <Link to="/" className="text-sm text-[#495057]" data-testid="register-back">← {t(lang,"common.back")}</Link>
          <LanguageSwitcher />
        </header>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md">
            <div className="editorial-rule pt-4">
              <h1 className="font-display text-4xl">{t(lang,"auth.create_title")}</h1>
            </div>
            <p className="mt-3 text-[#495057]">{t(lang,"auth.sub_up")}</p>

            <button
              onClick={googleSignIn}
              data-testid="google-register-btn"
              className="w-full mt-8 inline-flex items-center justify-center gap-3 border border-[#0A1128] text-[#0A1128] px-4 py-3 hover:bg-[#F8F9FA] transition-colors"
            >
              <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.616z"/><path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/><path fill="#FBBC05" d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z"/><path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z"/></svg>
              {t(lang,"auth.google")}
            </button>

            <div className="my-6 flex items-center gap-3">
              <div className="flex-1 h-px bg-[#E9ECEF]" />
              <span className="text-xs tracking-[0.2em] text-[#868E96]">{t(lang,"auth.or")}</span>
              <div className="flex-1 h-px bg-[#E9ECEF]" />
            </div>

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label className="text-xs tracking-[0.18em] uppercase text-[#868E96]">{t(lang,"auth.name")}</label>
                <input data-testid="register-name" required value={form.name} onChange={onChange("name")}
                  className="mt-2 w-full border border-[#E9ECEF] bg-[#F8F9FA] px-4 py-3 focus:outline-none focus:border-[#0A1128]" />
              </div>
              <div>
                <label className="text-xs tracking-[0.18em] uppercase text-[#868E96]">{t(lang,"auth.email")}</label>
                <input data-testid="register-email" type="email" required value={form.email} onChange={onChange("email")}
                  className="mt-2 w-full border border-[#E9ECEF] bg-[#F8F9FA] px-4 py-3 focus:outline-none focus:border-[#0A1128]" />
              </div>
              <div>
                <label className="text-xs tracking-[0.18em] uppercase text-[#868E96]">{t(lang,"auth.company")}</label>
                <input data-testid="register-company" value={form.company} onChange={onChange("company")}
                  className="mt-2 w-full border border-[#E9ECEF] bg-[#F8F9FA] px-4 py-3 focus:outline-none focus:border-[#0A1128]" />
              </div>
              <div>
                <label className="text-xs tracking-[0.18em] uppercase text-[#868E96]">{t(lang,"auth.password")}</label>
                <input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={onChange("password")}
                  className="mt-2 w-full border border-[#E9ECEF] bg-[#F8F9FA] px-4 py-3 focus:outline-none focus:border-[#0A1128]" />
              </div>
              {err && <p className="text-sm text-[#C92A2A]" data-testid="register-error">{err}</p>}
              <button
                type="submit" disabled={loading}
                data-testid="register-submit-btn"
                className="w-full bg-[#0A1128] text-white px-4 py-3 hover:bg-[#1C2538] transition-colors disabled:opacity-60"
              >
                {loading ? t(lang,"common.loading") : t(lang,"auth.sign_up")}
              </button>
            </form>

            <p className="mt-6 text-sm text-[#495057]">
              {t(lang,"auth.have_acc")} <Link to="/login" className="underline" data-testid="register-go-login">{t(lang,"auth.sign_in")}</Link>
            </p>
          </div>
        </div>
      </div>

      <div className="hidden lg:block lg:col-span-6 relative bg-[#0A1128]">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/6aa7d904-b7e4-4b7a-bd46-524404d838e7/images/8729cd92199028b967e8ab0e90b6fece9e1c5e7d5baec0d938d99a6808631edd.jpeg"
          alt="Signing a corporate agreement at a law office"
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128] via-[#0A1128]/30 to-transparent" />
        <div className="relative z-10 p-12 h-full flex flex-col justify-end text-white">
          <p className="text-xs tracking-[0.24em] uppercase text-[#C65D47] mb-3">Trusted by</p>
          <p className="font-display text-3xl leading-snug max-w-md">SMEs, in-house counsel and HR teams across Lebanon.</p>
        </div>
      </div>
    </div>
  );
}
